import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { ReminderService } from './reminder.service';
import { Reminder } from '../interfaces/reminder.interface';

const POLLING_INTERVAL_MS = 1 * 60 * 1000; // 1 perc

export interface ReminderToast {
  id: number;
  reminder: Reminder;
  displayName: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationPollingService implements OnDestroy {
  private pendingCountSubject = new BehaviorSubject<number>(0);
  private pendingRemindersSubject = new BehaviorSubject<Reminder[]>([]);
  private visibleToastsSubject = new BehaviorSubject<ReminderToast[]>([]);

  pendingCount$ = this.pendingCountSubject.asObservable();
  pendingReminders$ = this.pendingRemindersSubject.asObservable();
  visibleToasts$ = this.visibleToastsSubject.asObservable();

  private pollingSubscription: Subscription | null = null;
  private previousPendingIds = new Set<number>();
  private isPolling = false;

  constructor(
    private reminderService: ReminderService
  ) {}

  startPolling(): void {
    if (this.isPolling) return;
    this.isPolling = true;

    // Azonnal lekérdezzük
    this.fetchReminders();

    // 1 percenkénti polling
    this.pollingSubscription = interval(POLLING_INTERVAL_MS).pipe(
      switchMap(() => this.reminderService.getMyReminders().pipe(
        catchError(() => of({ success: false, reminders: [] as Reminder[], count: 0 }))
      ))
    ).subscribe(response => {
      if (response.success) {
        this.processReminders(response.reminders);
      }
    });
  }

  stopPolling(): void {
    this.isPolling = false;
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
    this.pendingCountSubject.next(0);
    this.pendingRemindersSubject.next([]);
    this.visibleToastsSubject.next([]);
    this.previousPendingIds.clear();
  }

  /** Kézi frissítés (pl. értesítés dismissal után) */
  refreshNow(): void {
    this.fetchReminders();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private fetchReminders(): void {
    this.reminderService.getMyReminders().subscribe({
      next: (response) => {
        if (response.success) {
          this.processReminders(response.reminders);
        }
      },
      error: () => {
        // Nem jelenítünk meg hibát, csendben folytatjuk
      }
    });
  }

  private processReminders(reminders: Reminder[]): void {
    this.pendingCountSubject.next(reminders.length);
    this.pendingRemindersSubject.next(reminders);

    // Új emlékeztetők detektálása → toast (csak pending státuszúakon, checked-eket kihagyjuk)
    const currentIds = new Set(reminders.map(r => r.id));
    const newReminders = reminders.filter(r => !this.previousPendingIds.has(r.id) && r.status === 'pending');

    // Minden új emlékeztetőhöz külön toast
    newReminders.forEach(r => this.addToast(r));

    // Toast-ok eltávolítása, ha az emlékeztető már nincs a pending listában
    const currentToasts = this.visibleToastsSubject.value;
    const filteredToasts = currentToasts.filter(t => currentIds.has(t.id));
    if (filteredToasts.length !== currentToasts.length) {
      this.visibleToastsSubject.next(filteredToasts);
    }

    this.previousPendingIds = currentIds;
  }

  private addToast(reminder: Reminder): void {
    const displayName = reminder.company_name || reminder.customer_name || reminder.title;
    const toast: ReminderToast = { id: reminder.id, reminder, displayName };
    const current = this.visibleToastsSubject.value;
    // Ne duplázzuk ugyanazt az emlékeztetőt
    if (!current.find(t => t.id === reminder.id)) {
      this.visibleToastsSubject.next([...current, toast]);
    }
  }

  dismissToast(reminderId: number): void {
    const current = this.visibleToastsSubject.value.filter(t => t.id !== reminderId);
    this.visibleToastsSubject.next(current);
    // Adatbázisban checked státuszra állítjuk — a menüben megmarad, de toast többé nem jelenik meg
    this.reminderService.updateReminderStatus(reminderId, 'checked').subscribe();
  }
}
